/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../components/atoms/Loader'
import RowEmpty from '../../components/atoms/RowEmpty'
import { BadgeStatusPrList } from '../../components/atoms/StatusLabel'
import TablePagination from '../../components/atoms/Table/TablePagination'
import api from '../../config/AxiosInstance'
import toast, { Toaster } from 'react-hot-toast'
import '../../styles/style.css'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const MANUAL_OPNAME_STATUSES = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'recheck', label: 'Recheck' },
  { value: 'done', label: 'Done' },
  { value: 'closed', label: 'Closed' }
]

function ManualStockOpname() {
  const { state } = useLocation()
  const navigate = useNavigate()

  const refSrcWarehouses = useRef()
  const refSrcStatus = useRef()

  const [warehouses, setWarehouses] = useState([])
  const [data, setData] = useState({
    values: [],
    metadata: {
      per_page: 10,
      current_page: 1,
      total_row: 0,
      total_page: 0
    }
  })

  const [warehouseId, setWarehouseId] = useState(state?.warehouse_id || '')
  const [q, setQ] = useState(state?.q || '')
  const [debouncedQ, setDebouncedQ] = useState(state?.q || '')
  const [status, setStatus] = useState(state?.status || '')

  const [loading, setLoading] = useState({
    data: false
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(q)
      setData((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, current_page: 1 }
      }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [q])

  const fetchData = async () => {
    if (!warehouseId) {
      setData({
        values: [],
        metadata: { ...data.metadata, total_row: 0, total_page: 0 }
      })
      return
    }
    try {
      setLoading((prevLoading) => ({ ...prevLoading, data: true }))
      const params = {
        q: debouncedQ,
        warehouse_id: warehouseId,
        status: status,
        type: 'manual',
        per_page: data?.metadata?.per_page || 10,
        page: data?.metadata?.current_page || 1
      }
      const res = await api.get('/stock-opname', { params })
      const { data: dataResponse, metadata: metadataResponse } = res.data
      setData({
        values: dataResponse,
        metadata: metadataResponse
      })
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      setData({ ...data, values: [] })
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, data: false }))
    }
  }

  useEffect(() => {
    fetchData()
  }, [
    data.metadata.current_page,
    data.metadata.per_page,
    warehouseId,
    debouncedQ,
    status
  ])

  const fetchWarehouses = async () => {
    try {
      const res = await api.get(`/master/warehouses/all`, {
        params: {
          is_parent: true,
          forUser: 1
        }
      })
      const { data: responseData } = res
      if (responseData.success) {
        const options = responseData.data.map((w) => ({
          value: w.id,
          label: w.warehouse_name
        }))
        setWarehouses(options)
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  useEffect(() => {
    fetchWarehouses()
  }, [])

  const handleReset = () => {
    setQ('')
    setDebouncedQ('')
    setWarehouseId('')
    setStatus('')
    refSrcWarehouses.current?.clearValue()
    refSrcStatus.current?.clearValue()
    setData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, current_page: 1 }
    }))
  }

  const handlePageChange = (data) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: data.selected + 1
      }
    }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: 1,
        per_page: parseInt(e.target.value, 10)
      }
    }))
    window.scrollTo(0, 0)
  }

  const columns = [
    { key: 'code', label: 'Ticket' },
    { key: 'warehouse_id', label: 'Warehouse' },
    {
      key: 'created_at',
      label: 'Created Date',
      className: 'text-end',
      component: (value) => value?.created_at || '-'
    },
    {
      key: 'finish_at',
      label: 'Finished Date',
      className: 'text-end',
      component: (value) =>
        value?.finish_date !== '-' ? value?.finish_date : '-'
    },
    {
      key: 'status',
      label: 'Status',
      className: 'text-end',
      component: (value) => (
        <div key={value?.id} className="d-flex justify-content-end">
          <BadgeStatusPrList
            text={value?.status ? value?.status.replace('_', ' ') : 'done'}
          />
        </div>
      )
    },
    {
      key: 'download',
      label: 'Berita Acara',
      className: 'text-end',
      component: (value) => (
        <div key={value?.id}>
          <Button
            variant="transparent"
            className={`${
              value?.status === 'finish'
                ? 'btn-primary-blue'
                : 'btn-secondary opacity-25'
            }`}
            disabled={value?.status !== 'finish'}
            size="sm"
          >
            DOWNLOAD
          </Button>
        </div>
      )
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => (
        <div key={value?.id}>
          <button
            className="btn btn-light text-secondary border rounded"
            style={{ width: '32px', height: '32px', padding: 0 }}
            onClick={() => {
              navigate(
                `/stock-opname/manual-stock-opname/detail/${value?.id}`,
                {
                  state: {
                    q,
                    warehouse_id: warehouseId,
                    status,
                    warehouse_name: value?.warehouse_id
                  }
                }
              )
            }}
          >
            <Search size={14} />
          </button>
        </div>
      )
    }
  ]

  return (
    <>
      <Toaster />
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Manual Stock Opname</h3>
          </Col>
          <Col className="text-end">
            <Button
              variant="light"
              className="text-secondary fw-bold rounded px-3 border"
              onClick={handleReset}
              style={{ fontSize: '0.85rem' }}
            >
              <RotateCw size={16} className="me-2" />
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Ticket</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={14} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search Ticket"
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                    onChange={(e) => {
                      setQ(e.target.value)
                    }}
                    value={q}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Warehouse</Form.Label>
                <Select
                  isClearable
                  ref={refSrcWarehouses}
                  options={warehouses}
                  styles={SelectStyles}
                  placeholder="All Warehouses"
                  onChange={(e) => {
                    setWarehouseId(e?.value || '')
                    setData((prev) => ({
                      ...prev,
                      metadata: { ...prev.metadata, current_page: 1 }
                    }))
                  }}
                  value={
                    warehouseId
                      ? warehouses.find((w) => w.value === warehouseId)
                      : null
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Status</Form.Label>
                <Select
                  isClearable
                  ref={refSrcStatus}
                  options={MANUAL_OPNAME_STATUSES}
                  styles={SelectStyles}
                  placeholder="All Statuses"
                  onChange={(e) => {
                    setStatus(e?.value || '')
                    setData((prev) => ({
                      ...prev,
                      metadata: { ...prev.metadata, current_page: 1 }
                    }))
                  }}
                  value={
                    status
                      ? MANUAL_OPNAME_STATUSES.find((s) => s.value === status)
                      : null
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row>
          <Col md={12} className="my-2">
            <Table
              hover
              responsive
              className="shadow-sm mb-5 bg-white"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                fontSize: '0.9rem'
              }}
            >
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      className={`p-2 align-middle ${column.className || ''}`}
                      key={column.key}
                      style={tableHeaderStyle}
                    >
                      <span>{column.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading.data && (
                  <tr>
                    <td colSpan={columns.length} className="p-5 text-center">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading.data && data?.values?.length === 0 && (
                  <RowEmpty colSpan={columns.length} />
                )}
                {!loading.data &&
                  data?.values?.length > 0 &&
                  data?.values?.map((item, index) => (
                    <tr key={`${item.id}-${index}`}>
                      <td className="p-2 align-middle fw-bold">
                        {item.code || '-'}
                      </td>
                      <td className="p-2 align-middle">{item.warehouse_id}</td>
                      <td className="p-2 align-middle text-end">
                        {item.created_at}
                      </td>
                      <td className="p-2 align-middle text-end">
                        {item.finish_date !== '-' ? item.finish_date : '-'}
                      </td>
                      <td className="p-2 align-middle text-end">
                        <BadgeStatusPrList
                          text={
                            item.status ? item.status.replace('_', ' ') : 'done'
                          }
                        />
                      </td>
                      <td className="p-2 align-middle text-end">
                        <Button
                          variant="transparent"
                          className={`${
                            item.status === 'finish'
                              ? 'btn-primary-blue'
                              : 'btn-secondary opacity-25'
                          }`}
                          disabled={item.status !== 'finish'}
                          size="sm"
                        >
                          DOWNLOAD
                        </Button>
                      </td>
                      <td className="p-2 align-middle text-end">
                        <button
                          className="btn btn-light text-secondary border rounded"
                          style={{ width: '32px', height: '32px', padding: 0 }}
                          onClick={() => {
                            navigate(
                              `/stock-opname/manual-stock-opname/detail/${item.id}`,
                              {
                                state: {
                                  q,
                                  warehouse_id: warehouseId,
                                  status,
                                  warehouse_name: item.warehouse_id
                                }
                              }
                            )
                          }}
                        >
                          <Search size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={columns.length} className="p-3 border-top-0">
                    <TablePagination
                      meta={data.metadata}
                      handlePageChange={handlePageChange}
                      handleOnChangeMeta={handleOnChangeMeta}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default ManualStockOpname
